import React, { useEffect, useState, useImperativeHandle, useRef } from "react";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { getCurrencySymbol } from "../../utils/currency/currencyUtils";
import baseUrl from "../../api/baseUrl";
import { getMediaUrl } from "../../utils/mediaUrl";
import {
  balance,
  billDiscountAmount,
  crossPay,
  discount,
  netTotal,
  tax,
  total,
} from "../../store/pos/posSelector";

interface ReceiptNewProps {
  shouldPrint?: boolean;
}

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York", // US Dollar
  "¥": "Asia/Tokyo", // Japanese Yen (default to Tokyo)
  "Rs.": "Asia/Colombo", // Sri Lankan Rupee
  "€": "Europe/Berlin", // Euro
  "£": "Europe/London", // British Pound
  "₹": "Asia/Kolkata", // Indian Rupee
  A$: "Australia/Sydney", // Australian Dollar
  C$: "America/Toronto", // Canadian Dollar
};

// Helper function to format phone number by removing country code
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");

  if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1);
  }

  const countryCodePatterns = [
    { code: "94", replace: "0" }, // Sri Lanka: +94 77 -> 077
    { code: "91", replace: "0" }, // India: +91 98 -> 098
    { code: "1", replace: "" }, // US/Canada: +1 555 -> 555
    { code: "44", replace: "0" }, // UK: +44 20 -> 020
    { code: "61", replace: "0" }, // Australia: +61 4 -> 04
    { code: "81", replace: "0" }, // Japan: +81 90 -> 090
    { code: "86", replace: "0" }, // China: +86 138 -> 0138
    { code: "49", replace: "0" }, // Germany: +49 30 -> 030
    { code: "33", replace: "0" }, // France: +33 6 -> 06
    { code: "39", replace: "0" }, // Italy: +39 3 -> 03
    { code: "971", replace: "0" }, // UAE: +971 50 -> 050
    { code: "966", replace: "0" }, // Saudi Arabia: +966 5 -> 05
    { code: "65", replace: "" }, // Singapore: +65 8 -> 8
    { code: "60", replace: "0" }, // Malaysia: +60 12 -> 012
  ];

  for (const pattern of countryCodePatterns) {
    if (cleaned.startsWith(pattern.code)) {
      return pattern.replace + cleaned.substring(pattern.code.length);
    }
  }

  if (!cleaned.startsWith("0") && cleaned.length >= 9) {
    return "0" + cleaned;
  }

  return cleaned;
};

// Format number to 2 decimal places
const formatNumber = (value: any) => Number(value || 0).toFixed(2);

const getNumericSetting = (setting: any, fallback: number) =>
  Number.isFinite(Number(setting?.size)) ? Number(setting.size) : fallback;

const getBooleanSetting = (setting: any, fallback = true) =>
  typeof setting?.show === "boolean" ? setting.show : fallback;

const getAmountSettingKey = (name: string): string | null => {
  const normalized = String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "");

  if (normalized === "total") return "amountTotal";
  if (normalized === "linediscount") return "amountLineDiscount";
  if (normalized === "tax") return "amountTax";
  if (normalized === "billdiscount") return "amountBillDiscount";
  if (normalized === "nettotal" || normalized === "n.amt")
    return "amountNetTotal";
  if (normalized === "card") return "amountCard";
  if (normalized === "cash") return "amountCash";
  if (normalized === "bank") return "amountBank";
  if (normalized === "totalpay") return "amountTotalPay";
  if (normalized === "balance") return "amountBalance";
  if (normalized === "pendingpayment") return "amountPendingPayment";

  return null;
};

const ReceiptNew = React.forwardRef<any, ReceiptNewProps>(
  ({ shouldPrint = false }, ref) => {
    const shopName = getDecryptedToken("martName");
    const address = getDecryptedToken("address");
    const phoneNumber = getDecryptedToken("phoneNumber");
    const name = getDecryptedToken("name");
    const email = getDecryptedToken("email");
    const isEmailShown = getDecryptedToken("isEmailShown") === "true";
    const registrationNumber = getDecryptedToken("registrationNumber");
    const logo = getDecryptedToken("logo");
    const currency = getDecryptedToken("currency");
    const currencySymbol = getCurrencySymbol();
    const [billSettings, setBillSettings] = useState<Record<string, any>>({});
    const printFnRef = useRef<() => void>(() => {});

    // Format the phone number
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber || "");

    // Extract number pattern (e.g., T5070003002742) from address
    const addressNumberMatch = address?.match(/(T\d{13,})/);
    const addressNumber = addressNumberMatch ? addressNumberMatch[1] : null;

    const {
      addedItemArray,
      card,
      cash,
      bank,
      billNumber,
      saleType,
      customerId,
      customerName,
    } = useSelector((state: RootState) => state.pos.current);

    const totalAmount = useSelector(total);
    const discountAmount = useSelector(discount);
    const taxAmount = useSelector(tax);
    const netTotalAmount = useSelector(netTotal);
    const computedBillDiscount = useSelector(billDiscountAmount);
    const lastbalance = useSelector(balance);
    const cpay = useSelector(crossPay);

    // Determine if this is a credit sale
    const isCredit = saleType === "credit" && customerId;

    // For credit sales with negative balance, we show it as a pending payment
    const pendingPayment =
      isCredit && lastbalance < 0 ? Math.abs(lastbalance) : 0;

    const displayNetTotal = Math.max(
      Number(netTotalAmount || 0) - Number(computedBillDiscount || 0),
      0,
    );

    const amount = [
      { value: totalAmount, name: "total" },
      { value: discountAmount, name: "Line discount" },
      { value: taxAmount, name: "tax" },
      { value: computedBillDiscount, name: "bill discount" },
      { value: displayNetTotal, name: "Net Total" },
      ...(Number(card) > 0 ? [{ value: card, name: "card" }] : []),
      ...(Number(cash) > 0 ? [{ value: cash, name: "cash" }] : []),
      ...(Number(bank) > 0 ? [{ value: bank, name: "bank" }] : []),
      { value: cpay, name: "totalPay" },
      isCredit && lastbalance < 0
        ? { value: pendingPayment, name: "pending payment" }
        : { value: lastbalance, name: "balance" },
    ];

    // Live date/time state
    const [currentTime, setCurrentTime] = useState(new Date());

    // Pick time zone based on currency symbol, fallback to browser time zone
    const timeZone =
      currencyTimeZoneMap[currency as string] ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    useEffect(() => {
      const timer = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => clearInterval(timer);
    }, []);

    useEffect(() => {
      const loadBillSettings = async () => {
        try {
          const response = await baseUrl.get(
            "branch/billformate/find-billformates",
          );
          const settings = response?.data?.data || [];
          const mappedSettings = settings.reduce(
            (acc: Record<string, any>, item: any) => {
              acc[item.title] = item;
              return acc;
            },
            {},
          );
          setBillSettings(mappedSettings);
        } catch (error) {
          setBillSettings({});
        }
      };

      loadBillSettings();
    }, []);

    // Auto-print if shouldPrint prop is true (for /bill route)
    useEffect(() => {
      if (shouldPrint && typeof printFnRef.current === "function") {
        const timer = setTimeout(() => {
          printFnRef.current();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [shouldPrint]);

    // Apply inline styles only when shop name is specific shops (case-insensitive)
    const applyInline =
      typeof shopName === "string" &&
      (shopName.trim().toLowerCase() === "mmk chiba" ||
        shopName.trim().toLowerCase() === "ayman bakery restaurant" ||
        shopName.trim().toLowerCase() === "world trading asian food");

    const headerSetting = billSettings.header || {};
    const logoSetting = billSettings.logo || {};
    const addressSetting = billSettings.address || {};
    const phoneSetting = billSettings.phone || {};
    const emailSetting = billSettings.emailText || {};
    const billNumberSetting = billSettings.orderNumber || {};
    const dateSetting = billSettings.date || {};
    const timeSetting = billSettings.time || {};
    const itemsHeaderSetting = billSettings.itemCode || {};
    const amountHeaderSetting = billSettings.totalDetails || {};
    const amountSettings = {
      amountTotal: billSettings.amountTotal || {},
      amountLineDiscount: billSettings.amountLineDiscount || {},
      amountTax: billSettings.amountTax || {},
      amountBillDiscount: billSettings.amountBillDiscount || {},
      amountNetTotal: billSettings.amountNetTotal || {},
      amountCard: billSettings.amountCard || {},
      amountCash: billSettings.amountCash || {},
      amountBank: billSettings.amountBank || {},
      amountTotalPay: billSettings.amountTotalPay || {},
      amountBalance: billSettings.amountBalance || {},
      amountPendingPayment: billSettings.amountPendingPayment || {},
    };
    const creditSaleSetting = billSettings.creditSaleBadge || {};
    const creditNoticeSetting = billSettings.creditPendingNotice || {};
    const thankYouSetting = billSettings.wish || {};

    const headerSize = getNumericSetting(headerSetting, 37);
    const logoSize = Math.max(getNumericSetting(logoSetting, 80), 24);
    const addressSize = Math.max(getNumericSetting(addressSetting, 13), 10);
    const phoneSize = Math.max(getNumericSetting(phoneSetting, 13), 10);
    const emailSize = Math.max(getNumericSetting(emailSetting, 13), 10);
    const billNumberSize = Math.max(
      getNumericSetting(billNumberSetting, 13),
      10,
    );
    const dateSize = Math.max(getNumericSetting(dateSetting, 11), 10);
    const timeSize = Math.max(getNumericSetting(timeSetting, 11), 10);
    const itemsHeaderSize = Math.max(
      getNumericSetting(itemsHeaderSetting, 10),
      10,
    );
    const amountSize = Math.max(getNumericSetting(amountHeaderSetting, 12), 10);
    const creditSaleSize = Math.max(
      getNumericSetting(creditSaleSetting, 12),
      10,
    );
    const creditNoticeSize = Math.max(
      getNumericSetting(creditNoticeSetting, 10),
      10,
    );
    const thankYouSize = Math.max(getNumericSetting(thankYouSetting, 18), 12);

    // Function to generate and print receipt
    printFnRef.current = () => {
      // Build items HTML
      const itemsHtml = (addedItemArray || [])
        .map((item: any, i: number) => {
          return `
            <div class="r_items">
              <div class="row1">
                <span>${String(i + 1).padStart(2, "0")}.</span>
                <span>${(item?.name || "").replace(/</g, "&lt;")}</span>
              </div>
              <div class="row2">
                <span>${item?.quantity || 0}</span>
                <span>${currencySymbol} ${formatNumber(item?.price)}</span>
                <span>X</span>
                <span>${formatNumber(item?.discount)}</span>
                <span>${currencySymbol} ${formatNumber(item?.finalPrice)}</span>
              </div>
            </div>
          `;
        })
        .join("");

      // Build amounts HTML
      const amountsHtml = amount
        .map((a: any) => {
          const label = String(a?.name || "")
            .toLowerCase()
            .replace(/\s+/g, "");
          const isNetTotal = label === "nettotal" || label === "n.am";
          const amountKey = getAmountSettingKey(a?.name || "");
          const rowSetting = amountKey
            ? (amountSettings as any)[amountKey]
            : null;
          const rowVisible = rowSetting
            ? getBooleanSetting(rowSetting, true)
            : true;
          if (!rowVisible) {
            return "";
          }

          const rowSize = rowSetting
            ? Math.max(getNumericSetting(rowSetting, amountSize), 10)
            : amountSize;
          const rowTop = rowSetting ? Number(rowSetting?.top ?? 0) : 0;
          const rowLeft = rowSetting ? Number(rowSetting?.left ?? 0) : 0;

          return `
            <div class="datas${isNetTotal ? " nettotal-row" : ""}" style="font-size: ${rowSize}px; position: relative; transform: translate(${rowLeft}px, ${rowTop}px);">
              <span>${(a?.name || "").replace(/</g, "&lt;")}</span>
              <span>${formatNumber(a?.value)}</span>
            </div>
          `;
        })
        .join("");

      // Build logo HTML if available
      const logoHtml =
        logo && logo !== "" && getBooleanSetting(logoSetting)
          ? `<div class="logo">
              <img src="${getMediaUrl(logo)}" alt="Shop Logo" style="width: ${logoSize}px; height: ${logoSize}px; object-fit: contain; border: 1px solid #e2e8f0; border-radius: 4px; padding: 4px; margin-bottom: 10px;" />
            </div>`
          : "";

      // Build registration number HTML if available
      const registrationHtml = registrationNumber
        ? `<div class="address-number" style="font-size: ${billNumberSize}px;">Reg No: ${(registrationNumber || "").replace(/</g, "&lt;")}</div>`
        : "";

      // Build address number HTML if available
      const addressNumberHtml = addressNumber
        ? `<div class="address-number" style="font-size: ${billNumberSize}px;">${(addressNumber || "").replace(/</g, "&lt;")}</div>`
        : "";

      // Build email HTML if available
      const emailHtml =
        isEmailShown && email && getBooleanSetting(emailSetting)
          ? `<div class="email" style="font-size: ${emailSize}px;">${(email || "").replace(/</g, "&lt;")}</div>`
          : "";

      // Build customer name HTML if available
      const customerHtml =
        customerId && customerName
          ? `<div class="customer-name">Customer: ${(customerName || "").replace(/</g, "&lt;")}</div>`
          : "";

      // Build credit sale badge if applicable
      const creditHtml = isCredit
        ? `<div class="sale-type credit" style="font-size: ${creditSaleSize}px; position: relative; transform: translate(${Number(creditSaleSetting?.left ?? 0)}px, ${Number(creditSaleSetting?.top ?? 0)}px);${getBooleanSetting(creditSaleSetting, true) ? "" : "display:none;"}">CREDIT SALE</div>`
        : "";

      // Build credit notice if applicable
      const creditNoticeHtml =
        isCredit && pendingPayment > 0
          ? `<div class="credit-notice" style="font-size: ${creditNoticeSize}px; position: relative; transform: translate(${Number(creditNoticeSetting?.left ?? 0)}px, ${Number(creditNoticeSetting?.top ?? 0)}px);${getBooleanSetting(creditNoticeSetting, true) ? "" : "display:none;"}">
              <p style="font-size: inherit;">Amount pending: ${currencySymbol} ${formatNumber(pendingPayment)}</p>
              <p style="font-size: inherit;">Please settle your account soon.</p>
            </div>`
          : "";

      // Date/time HTML
      const dateTimeHtml = `${currentTime.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone,
      })} ${currentTime.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone,
      })}`;

      const receiptHtml = `
        <!doctype html>
        <html>
        <head>
        <meta charset="utf-8"/>
        <title>Receipt</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0;
            padding: 0;
          }
          * {
            margin: 0;
            padding: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }
          .receipt {
            background-color: white;
            color: black;
            width: 350px;
            margin: 0;
            padding: 5px 8px;
            box-sizing: border-box;
            display: block;
          }
          .receipt > * {
            font-family: "Stick No Bills", Arial, sans-serif;
            text-transform: uppercase;
            background-color: transparent;
            color: inherit;
          }
          .receipt.mmk-chiba > * {
            font-family: "Arial Narrow", Arial, sans-serif !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
          }
          .receipt > div {
            padding: 2px 0;
          }
          .receipt hr {
            border: 1px dotted;
          }
          .receipt .headers {
            background-color: white;
            color: black;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .receipt .headers .logo {
            margin-bottom: 10px;
          }
          .receipt .headers .title {
            font-size: 37px;
            text-align: center;
            word-wrap: break-word;
            width: 100%;
            overflow-wrap: break-word;
          }
          .receipt .headers .address {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .headers .address-number {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .headers .tell {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .headers .email {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .headers .bilNUmber {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .headers .customer-name {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
            font-weight: 700;
          }
          .receipt .headers .time_r {
            background-color: white !important;
            color: black;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 4px;
          }
          .receipt .headers .date_r,
          .receipt .headers .name_r {
            background-color: white !important;
            color: black;
            font-size: 11px;
            background-color: transparent;
            color: black;
          }
          .receipt .heading {
            background-color: white;
            color: black;
          }
          .receipt .heading .row1 {
            display: flex;
            justify-content: flex-start;
            gap: 6px;
          }
          .receipt .heading .row2 {
            display: flex;
            justify-content: space-between;
            gap: 1px;
            font-size: 10.5px;
            white-space: nowrap;
            padding: 0 1px;
          }
          .receipt .r_items {
            background-color: white;
            color: black;
          }
          .receipt .r_items .row1 {
            display: flex;
            justify-content: flex-start;
            gap: 6px;
          }
          .receipt .r_items .row2 {
            display: flex;
            justify-content: space-between;
            gap: 1px;
            font-size: 10.5px;
            white-space: nowrap;
            overflow-wrap: break-word;
            padding: 0 1px;
          }
          .receipt .amounts {
            display: flex;
            flex-direction: column;
            width: 100%;
            background-color: white;
            color: black;
          }
          .receipt .amounts .datas {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            gap: 1px;
            padding: 0 1px;
          }
          .receipt .amounts .datas.nettotal-row {
            font-weight: 700;
          }
          .receipt .thank {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 50px;
            font-size: 18px;
          }
          .receipt .sale-type {
            font-weight: 700;
            text-align: center;
            margin: 5px 0;
            font-size: 12px;
          }
          .receipt .sale-type.credit {
            color: #4CAF50;
            border: 1px dashed #4CAF50;
            padding: 2px 0;
          }
          .receipt .credit-notice {
            margin: 10px 0;
            text-align: center;
            color: #4CAF50;
            font-weight: 700;
            border-top: 1px dashed #4CAF50;
            border-bottom: 1px dashed #4CAF50;
            padding: 5px 0;
          }
          .receipt .credit-notice p {
            margin: 2px 0;
            font-size: 10px;
          }
          @media print {
            @page {
              size: 80mm auto;
              margin: 0 !important;
              padding: 0 !important;
            }
            * {
              margin: 0 !important;
              padding: 0 !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 100% !important;
              display: flex !important;
              justify-content: center !important;
              align-items: flex-start !important;
            }
            .receipt {
              width: 80mm !important;
              min-width: 80mm !important;
              max-width: 80mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              box-shadow: none !important;
              display: block !important;
            }
            .receipt {
              padding: 5px 8px !important;
            }
            .receipt > div {
              padding: 2px 0 !important;
            }
          .receipt .headers .title {
            font-size: 32px;
          }
            .receipt .thank {
              font-size: 14px !important;
              height: 32px !important;
            }
            .receipt .headers .logo img {
              width: 60px !important;
              height: 60px !important;
            }
          }
        </style>
        </head>
        <body>
        <div class="receipt ${applyInline ? "mmk-chiba" : ""}">
          <div class="headers">
            ${logoHtml}
            <div class="title" style="font-size: ${headerSize}px;${getBooleanSetting(headerSetting) ? "" : "display:none;"}">${(shopName || "").replace(/</g, "&lt;")}</div>
            <div class="address" style="font-size: ${addressSize}px;${getBooleanSetting(addressSetting) ? "" : "display:none;"}">${(address || "").replace(/</g, "&lt;")}</div>
            ${addressNumberHtml}
            ${registrationHtml}
            <div class="tell" style="font-size: ${phoneSize}px;${getBooleanSetting(phoneSetting) ? "" : "display:none;"}">${formattedPhoneNumber}</div>
            ${emailHtml}
            <div class="bilNUmber" style="font-size: ${billNumberSize}px;${getBooleanSetting(billNumberSetting) ? "" : "display:none;"}">${billNumber || ""}</div>
            ${customerHtml}
            ${creditHtml}
            <div class="time_r">
              <div class="date_r" style="font-size: ${dateSize}px;${getBooleanSetting(dateSetting) ? "" : "display:none;"}">${dateTimeHtml}</div>
              <div class="name_r" style="font-size: ${timeSize}px;${getBooleanSetting(timeSetting) ? "" : "display:none;"}">${(name || "").replace(/</g, "&lt;")}</div>
            </div>
          </div>
          <hr />
          <div class="heading">
            <div class="row1">
              <span style="font-size: ${itemsHeaderSize}px;">Ln</span>
              <span style="font-size: ${itemsHeaderSize}px;">items</span>
            </div>
            <div class="row2">
              <span style="font-size: ${itemsHeaderSize}px;">qty</span>
              <span style="font-size: ${itemsHeaderSize}px;">rate</span>
              <span style="font-size: ${itemsHeaderSize}px;">gross</span>
              <span style="font-size: ${itemsHeaderSize}px;">dis%</span>
              <span style="font-size: ${itemsHeaderSize}px;">n.amt</span>
            </div>
          </div>
          <hr />
          ${itemsHtml}
          <hr />
          <div class="amounts" style="font-size: ${amountSize}px;">
            ${amountsHtml}
          </div>
          <hr />
          ${creditNoticeHtml}
          <div class="thank" style="font-size: ${thankYouSize}px;${getBooleanSetting(thankYouSetting) ? "" : "display:none;"}">Thank You! Come again</div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              // Trigger print dialog
              window.print();
              // Close window after print (works differently on different browsers)
              window.onafterprint = function() {
                window.close();
              };
              // Fallback close after 2 seconds if onafterprint doesn't work
              setTimeout(function() {
                window.close();
              }, 2000);
            }, 150);
          };
        </script>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank", "width=400,height=800");
      if (!printWindow) {
        console.error("Unable to open print window. Please allow popups.");
        return;
      }
      printWindow.document.open();
      printWindow.document.write(receiptHtml);
      printWindow.document.close();
    };

    // Expose print function via useImperativeHandle
    useImperativeHandle(ref, () => ({
      print: () => printFnRef.current(),
    }));

    return null;
  },
);

ReceiptNew.displayName = "ReceiptNew";

export default ReceiptNew;
